import React, { useState, useEffect, useCallback } from "react";
import { Service } from "./Service";
import { ExecutionService } from "./ExecutionService";
import { Row, Col, Button, Card, CardHeader, CardBody, Badge, Spinner, Alert } from "reactstrap";
import axios from 'axios';

const ACTIVE_RUN_STORAGE_KEY = 'microprod_active_run';

const Run = () => {
    const [commands, setCommands] = useState([]);
    const [command, setCommand] = useState({});
    const [step, setStep] = useState(0);
    const service = React.useMemo(() => new Service(), []);
    const executionService = React.useMemo(() => new ExecutionService(), []);
    const [ingredients, setIngredients] = useState([]);
    const [recipies, setRecipies] = useState([]);
    const [allExecutions, setAllExecutions] = useState([]);
    const [qtys, setQtys] = useState([]);
    const [weight, setWeight] = useState(0);
    const [actualQtys, setActualQtys] = useState([]);
    const [recipy, setRecipy] = useState(null);
    const [currentExecution, setCurrentExecution] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tempWeighings, setTempWeighings] = useState([]);
    const [scaleConnected, setScaleConnected] = useState(true);
    const [resumedNotice, setResumedNotice] = useState(null);

    const saveActiveRunToStorage = (cmd, exec, currentStep, currentActualQtys, currentTempWeighings) => {
        if (!cmd || !cmd.id || !cmd.executing) return;
        try {
            const state = {
                commandId: cmd.id,
                executionId: exec ? exec.id : null,
                step: currentStep || 0,
                actualQtys: currentActualQtys || [],
                tempWeighings: currentTempWeighings || [],
                savedAt: new Date().toISOString()
            };
            localStorage.setItem(ACTIVE_RUN_STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Error saving active run to localStorage:', e);
        }
    };

    const clearActiveRunStorage = () => {
        try {
            localStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
        } catch (e) {
            console.error('Error clearing active run from localStorage:', e);
        }
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [ingredientsData, recipiesData, commandsData, executionsData] = await Promise.all([
                service.getIngredients(),
                service.getRecipies(),
                service.getCommands(),
                executionService.getCommandExecutions()
            ]);
            setIngredients(ingredientsData || []);
            setRecipies(recipiesData || []);
            setCommands(commandsData || []);
            setAllExecutions(executionsData || []);

            // Check if there is an in-progress run to resume automatically
            let savedState = null;
            try {
                const rawSaved = localStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
                if (rawSaved) savedState = JSON.parse(rawSaved);
            } catch (e) {
                console.error('Error reading saved run from localStorage:', e);
            }

            const activeBackendExecution = (executionsData || []).find(e => e.status === 'InProgress');
            const targetCommandId = savedState?.commandId || activeBackendExecution?.commandId;

            if (targetCommandId) {
                const targetCmd = (commandsData || []).find(c => c.id === targetCommandId && !c.completed && c.status !== 'Completed');
                if (targetCmd) {
                    const targetRecipeId = targetCmd.recipeId || targetCmd.recipyId;
                    const rec = (recipiesData || []).find(r => r.id === targetRecipeId);
                    if (rec) {
                        const analogy = (rec.qty && rec.qty > 0) ? (targetCmd.qty / rec.qty) : 1;
                        const scaledQtys = (rec.ingredients || []).filter(Boolean).map(ing => ({
                            ...ing,
                            qty: Math.round(ing.qty * analogy)
                        }));

                        let execToUse = activeBackendExecution && activeBackendExecution.commandId === targetCommandId 
                            ? activeBackendExecution 
                            : null;

                        if (!execToUse && savedState?.executionId) {
                            execToUse = (executionsData || []).find(e => e.id === savedState.executionId);
                        }

                        let restoredStep = 0;
                        let restoredActuals = [];
                        let restoredTemp = [];

                        if (savedState && savedState.commandId === targetCommandId) {
                            restoredStep = savedState.step || 0;
                            restoredActuals = savedState.actualQtys || [];
                            restoredTemp = savedState.tempWeighings || [];
                        } else if (execToUse) {
                            restoredStep = execToUse.currentStep || 0;
                            restoredActuals = (execToUse.ingredients || []).map(i => ({
                                id: i.ingredientId,
                                ingredientId: i.ingredientId,
                                qty: i.actualQuantity
                            }));
                        }

                        setRecipy(rec);
                        setQtys(scaledQtys);
                        setCurrentExecution(execToUse);
                        setStep(restoredStep);
                        setActualQtys(restoredActuals);
                        setTempWeighings(restoredTemp);
                        setCommand({
                            ...targetCmd,
                            executing: true
                        });
                        setResumedNotice(`Ανακτήθηκε αυτόματα η εντολή "${targetCmd.title}" (Βήμα ${restoredStep + 1} από ${scaledQtys.length}).`);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    }, [service, executionService]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        let interval = null;
        if (command && command.executing) {
            interval = setInterval(() => {
                axios.get('http://localhost:3001')
                    .then(r => {
                        setScaleConnected(true);
                        if (r.data && (r.data.weight || r.data.weight === 0)) {
                            setWeight(Number(r.data.weight) || 0);
                        }
                    })
                    .catch(() => {
                        setScaleConnected(false);
                    });
            }, 800);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [command]);

    const setZero = () => {
        axios.post('http://localhost:3001/zero?' + new Date().getTime(), { dateTime: new Date().getTime() }, { cache: false })
            .catch(e => console.error('Zero error:', e));
    };

    const selectCommand = async (c) => {
        try {
            setLoading(true);
            let executions = [];
            try {
                executions = await executionService.getCommandExecutionsByCommandId(c.id);
            } catch (err) {
                console.warn('Could not fetch executions by command id:', err);
            }
            const activeExecution = (executions || []).find(e => e.status === 'InProgress');

            let savedState = null;
            try {
                const rawSaved = localStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
                if (rawSaved) savedState = JSON.parse(rawSaved);
            } catch (e) {
                console.error('Error reading active run from localStorage:', e);
            }

            const recId = c.recipeId || c.recipyId;
            const rec = recipies.find(r => r.id === recId);
            let scaledQtys = [];
            if (rec) {
                setRecipy(rec);
                const analogy = (rec.qty && rec.qty > 0) ? (c.qty / rec.qty) : 1;
                scaledQtys = (rec.ingredients || []).filter(Boolean).map(ing => ({
                    ...ing,
                    qty: Math.round(ing.qty * analogy)
                }));
                setQtys(scaledQtys);
            } else {
                setRecipy(null);
                setQtys([]);
            }

            if (activeExecution || (savedState && savedState.commandId === c.id)) {
                const restoredStep = savedState?.commandId === c.id 
                    ? (savedState.step || 0) 
                    : (activeExecution?.currentStep || 0);
                
                const restoredActuals = savedState?.commandId === c.id && savedState.actualQtys?.length > 0
                    ? savedState.actualQtys
                    : (activeExecution?.ingredients || []).map(i => ({
                        id: i.ingredientId,
                        ingredientId: i.ingredientId,
                        qty: i.actualQuantity
                    }));
                
                const restoredTemps = savedState?.commandId === c.id ? (savedState.tempWeighings || []) : [];

                setCurrentExecution(activeExecution || (savedState?.executionId ? { id: savedState.executionId } : null));
                setStep(restoredStep);
                setActualQtys(restoredActuals);
                setTempWeighings(restoredTemps);

                const executingCmd = { ...c, executing: true };
                setCommand(executingCmd);
                saveActiveRunToStorage(executingCmd, activeExecution, restoredStep, restoredActuals, restoredTemps);
            } else {
                setCurrentExecution(null);
                setActualQtys([]);
                setStep(0);
                setTempWeighings([]);
                setCommand({ ...c, executing: false });
            }
        } catch (error) {
            console.error('Error selecting command:', error);
            setCommand({ ...c, executing: false });
        } finally {
            setLoading(false);
        }
    };

    const executeCommand = async () => {
        try {
            setLoading(true);
            setTempWeighings([]);
            
            let execution = currentExecution;
            if (!execution || !execution.id) {
                try {
                    execution = await executionService.startCommandExecution(command.id);
                    setCurrentExecution(execution);
                } catch (err) {
                    console.warn('Could not record backend execution start, running locally:', err);
                    execution = { id: 'local_' + new Date().getTime(), commandId: command.id, currentStep: step };
                    setCurrentExecution(execution);
                }
            }
            
            const recId = command.recipeId || command.recipyId;
            const rec = recipies.find(r => r.id === recId) || recipy;
            let scaledQtys = qtys;
            if (rec) {
                setRecipy(rec);
                const analogy = (rec.qty && rec.qty > 0) ? (command.qty / rec.qty) : 1;
                scaledQtys = (rec.ingredients || []).filter(Boolean).map(ing => ({
                    ...ing,
                    qty: Math.round(ing.qty * analogy)
                }));
                setQtys(scaledQtys);
            }
            
            const updatedCommand = {
                ...command, 
                executing: true
            };
            setCommand(updatedCommand);
            saveActiveRunToStorage(updatedCommand, execution, step, actualQtys, []);
        } catch (error) {
            console.error('Error starting command execution:', error);
        } finally {
            setLoading(false);
        }
    };

    const completeCommand = async () => {
        try {
            setLoading(true);
            setTempWeighings([]);
            
            if (currentExecution && currentExecution.id) {
                await executionService.completeCommandExecution(currentExecution.id);
            }
            
            const completedCmd = {
                ...command,
                executing: false,
                completed: true,
                status: 'Completed',
                qtys: actualQtys,
                weight: actualQtys.reduce((q, qq) => q + (Number(qq.qty) || 0), 0)
            };
            
            setCommand(completedCmd);
            clearActiveRunStorage();
            await service.recordProduction(completedCmd);
            await loadData();
        } catch (error) {
            console.error('Error completing command execution:', error);
        } finally {
            setLoading(false);
        }
    };

    const pauseAndExit = () => {
        if (command && command.executing) {
            saveActiveRunToStorage(command, currentExecution, step, actualQtys, tempWeighings);
        }
        setCommand({});
    };

    const cancelAndResetRun = () => {
        const confirmReset = window.confirm("Είστε βέβαιοι ότι θέλετε να ακυρώσετε την τρέχουσα εκτέλεση; Η πρόοδος ζύγισης θα διαγραφεί.");
        if (confirmReset) {
            clearActiveRunStorage();
            setCommand({});
            setCurrentExecution(null);
            setStep(0);
            setActualQtys([]);
            setTempWeighings([]);
            setResumedNotice(null);
            loadData();
        }
    };

    const addTempWeighing = () => {
        const nextTemps = [...tempWeighings, weight];
        setTempWeighings(nextTemps);
        saveActiveRunToStorage(command, currentExecution, step, actualQtys, nextTemps);
        setZero();
    };

    const getTotalWeight = () => {
        return tempWeighings.reduce((a, b) => a + b, 0) + weight;
    };

    const currentTargetQty = qtys[step] ? qtys[step].qty : 0;
    const currentTotalWeight = getTotalWeight();

    const isNextStepAvailable = () => {
        if (!qtys[step] || currentTargetQty <= 0) return false;
        const tolerance = currentTargetQty < 4000 ? 0.015 : 0.025; // 1.5% - 2.5% tolerance
        const minAcceptable = currentTargetQty * (1 - tolerance);
        const maxAcceptable = currentTargetQty * (1 + tolerance);
        return currentTotalWeight >= minAcceptable && currentTotalWeight <= maxAcceptable;
    };

    const isFinishAvailable = () => {
        return qtys.length > 0 && actualQtys.length === qtys.length;
    };

    const nextStep = async () => {
        if (isNextStepAvailable()) {
            try {
                const totalWeight = getTotalWeight();
                let aqtys = [...actualQtys];
                const existingIdx = aqtys.findIndex(aq => aq.ingredientId === qtys[step].ingredientId);
                
                if (existingIdx >= 0) {
                    aqtys[existingIdx] = {
                        ...aqtys[existingIdx],
                        qty: totalWeight
                    };
                } else {
                    aqtys.push({
                        ...qtys[step],
                        qty: totalWeight
                    });
                }
                setActualQtys(aqtys);
                setTempWeighings([]);
                
                const nextStepIndex = step < qtys.length - 1 ? step + 1 : step;
                saveActiveRunToStorage(command, currentExecution, nextStepIndex, aqtys, []);

                if (currentExecution && currentExecution.id) {
                    const executionIngredients = aqtys.map(aq => 
                        executionService.createExecutionIngredient(
                            aq.ingredientId, 
                            qtys.find(q => q.ingredientId === aq.ingredientId)?.qty || 0,
                            aq.qty,
                            'g'
                        )
                    );
                    
                    const updatedExecution = await executionService.updateCommandExecution({
                        id: currentExecution.id,
                        status: 'InProgress',
                        currentStep: nextStepIndex,
                        ingredients: executionIngredients
                    });
                    setCurrentExecution(updatedExecution);
                }
                
                if (step < qtys.length - 1) {
                    setStep(step + 1);
                }
                setZero();
            } catch (error) {
                console.error('Error updating execution step:', error);
            }
        } else {
            window.alert("Το βάρος δεν βρίσκεται εντός των αποδεκτών ορίων της συνταγής.");
        }
    };

    const currentIngredient = qtys[step] ? ingredients.find(ing => ing.id === qtys[step].ingredientId) : null;
    const progressPercent = currentTargetQty > 0 ? Math.min(Math.round((currentTotalWeight / currentTargetQty) * 100), 120) : 0;

    return (
        <div className="run-container">
            {/* 1. Selection screen when no command is active */}
            {!command.id && (
                <div>
                    {resumedNotice && (
                        <Alert color="info" className="d-flex align-items-center justify-content-between mb-4 shadow-sm" toggle={() => setResumedNotice(null)}>
                            <div>
                                <i className="bi bi-info-circle-fill me-2 fs-5"></i>
                                <span>{resumedNotice}</span>
                            </div>
                        </Alert>
                    )}

                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h4 className="mb-0 text-dark fw-bold">
                            <i className="bi bi-play-circle-fill text-primary me-2"></i> Επιλογή Εντολής προς Εκτέλεση
                        </h4>
                        <Badge color="info" pill>{commands.filter(c => !c.completed && c.status !== 'Completed').length} εκκρεμείς</Badge>
                    </div>

                    {loading && commands.length === 0 ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Φόρτωση εντολών παραγωγής...</p>
                        </div>
                    ) : commands.filter(c => !c.completed && c.status !== 'Completed').length === 0 ? (
                        <div className="ek-card p-5 text-center">
                            <i className="bi bi-check2-all text-success" style={{ fontSize: '3.5rem' }}></i>
                            <h4 className="mt-3">Δεν υπάρχουν εκκρεμείς εντολές</h4>
                            <p className="text-muted">Όλες οι εντολές έχουν εκτελεστεί. Δημιουργήστε νέα εντολή από το μενού "Εντολές Παραγωγής".</p>
                        </div>
                    ) : (
                        <Row className="g-4">
                            {commands.filter(c => !c.completed && c.status !== 'Completed').map((c) => {
                                const r = recipies.find(rc => rc.id === (c.recipeId || c.recipyId));
                                const inProgExec = allExecutions.find(e => e.commandId === c.id && e.status === 'InProgress');
                                return (
                                    <Col lg="4" md="6" key={c.id}>
                                        <Card className="ek-card h-100" style={{ cursor: 'pointer' }} onClick={() => selectCommand(c)}>
                                            <CardHeader className="ek-card-header">
                                                <h5 className="text-truncate">{c.title}</h5>
                                                <div className="d-flex gap-2 align-items-center">
                                                    {inProgExec && (
                                                        <Badge color="warning" className="text-dark">
                                                            <i className="bi bi-arrow-repeat me-1"></i> Σε Εξέλιξη (Βήμα {(inProgExec.currentStep || 0) + 1})
                                                        </Badge>
                                                    )}
                                                    <Badge className="badge-brand">{c.qty} gr</Badge>
                                                </div>
                                            </CardHeader>
                                            <CardBody className="ek-card-body d-flex flex-column justify-content-between">
                                                <div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Συνταγή:</small>
                                                        <strong>{r ? r.title : (c.recipeTitle || 'Μη επιλεγμένη συνταγή')}</strong>
                                                    </div>
                                                    {c.description && (
                                                        <p className="text-muted small mb-3">{c.description}</p>
                                                    )}
                                                </div>
                                                <Button color={inProgExec ? "warning" : "primary"} block className="mt-3 fw-bold">
                                                    {inProgExec ? (
                                                        <>
                                                            <i className="bi bi-play-circle-fill me-1"></i> Συνέχιση Εκτέλεσης
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-play-circle me-1"></i> Έναρξη
                                                        </>
                                                    )}
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </div>
            )}

            {/* 2. Command Details Preview before starting execution */}
            {command.id && !command.executing && !command.completed && (
                <div className="ek-card">
                    <CardHeader className="ek-card-header">
                        <h5>
                            <i className="bi bi-clipboard2-check text-primary"></i> Προετοιμασία Εκτέλεσης: {command.title}
                        </h5>
                        <Button color="outline-primary" size="sm" onClick={() => setCommand({})}>
                            <i className="bi bi-arrow-left"></i> Επιστροφή
                        </Button>
                    </CardHeader>
                    <CardBody className="ek-card-body">
                        <Row className="mb-4">
                            <Col md="6">
                                <div className="p-3 bg-light rounded-3 mb-3 border">
                                    <span className="text-muted d-block small">Συνταγή Παραγωγής</span>
                                    <h4 className="text-primary mb-1">{recipy?.title || command.recipeTitle || 'Συνταγή'}</h4>
                                    <p className="text-muted small mb-0">{recipy?.description || 'Χωρίς περιγραφή'}</p>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="p-3 bg-light rounded-3 mb-3 border">
                                    <span className="text-muted d-block small">Συνολικό Βάρος Παρτίδας</span>
                                    <h4 className="text-success mb-1">{command.qty} gr</h4>
                                    <p className="text-muted small mb-0">{qtys.length} συστατικά προς ζύγιση</p>
                                </div>
                            </Col>
                        </Row>

                        <h5 className="mb-3">
                            <i className="bi bi-list-check text-primary me-2"></i> Συστατικά Συνταγής
                        </h5>

                        {qtys.length === 0 ? (
                            <Alert color="warning" className="d-flex align-items-center mb-4">
                                <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                                <span>Η συνταγή δεν περιέχει συστατικά. Παρακαλούμε προσθέστε συστατικά στη συνταγή πριν την εκτέλεση.</span>
                            </Alert>
                        ) : (
                            <div className="table-responsive mb-4">
                                <table className="table table-hover border rounded-3">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Συστατικό</th>
                                            <th>Κωδικός</th>
                                            <th className="text-end">Ποσότητα (gr)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {qtys.map((q, idx) => {
                                            const ing = ingredients.find(i => i.id === q.ingredientId);
                                            return (
                                                <tr key={idx}>
                                                    <td><span className="badge bg-secondary">{idx + 1}</span></td>
                                                    <td><strong>{ing ? ing.title : (q.ingredientTitle || 'Συστατικό')}</strong></td>
                                                    <td><code>{ing?.code || '-'}</code></td>
                                                    <td className="text-end fw-bold text-primary">{q.qty} gr</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                            <Button color="secondary" onClick={() => setCommand({})}>
                                Ακύρωση
                            </Button>
                            <Button color="primary" size="lg" onClick={executeCommand} disabled={loading || qtys.length === 0}>
                                <i className="bi bi-play-circle-fill"></i> Έναρξη Ζύγισης & Εκτέλεσης
                            </Button>
                        </div>
                    </CardBody>
                </div>
            )}

            {/* 3. Live Scale & Weighing Terminal */}
            {command.id && command.executing && (
                qtys.length > 0 ? (
                <Row className="g-4">
                    <Col lg="8">
                        <div className="scale-monitor-card">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`ek-status-dot ${scaleConnected ? '' : 'bg-danger'}`}></span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                        {scaleConnected ? 'Ηλεκτρονική Ζυγαριά Συνδεδεμένη' : 'Αποσυνδεδεμένη Ζυγαριά'}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Badge color="light" className="text-dark small border">
                                        <i className="bi bi-cloud-check text-success me-1"></i> Αυτόματη Αποθήκευση
                                    </Badge>
                                    <Button color="warning" size="sm" onClick={setZero} className="fw-bold">
                                        <i className="bi bi-arrow-counterclockwise"></i> Μηδενισμός (Tare)
                                    </Button>
                                </div>
                            </div>

                            {/* Active Ingredient Target Box */}
                            <div className="scale-target-box mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span style={{ color: '#aae0ff', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Τρέχον Υλικό (Βήμα {step + 1} / {qtys.length})
                                    </span>
                                    <Badge color={isNextStepAvailable() ? 'success' : 'warning'}>
                                        {isNextStepAvailable() ? 'Εντός Ορίων ✓' : 'Απαιτείται Ζύγιση'}
                                    </Badge>
                                </div>
                                <h2 style={{ fontSize: '1.85rem', fontWeight: 800, margin: 0, color: '#ffffff' }}>
                                    {currentIngredient ? currentIngredient.title : 'Συστατικό'}
                                </h2>
                                <div className="d-flex align-items-baseline gap-2 mt-2">
                                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>Στόχος Συνταγής:</span>
                                    <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#38bdf8' }}>
                                        {currentTargetQty} gr
                                    </span>
                                </div>
                            </div>

                            {/* Big Digital Scale Readout */}
                            <div className="text-center py-2">
                                <div className="scale-digit-display">
                                    {currentTotalWeight.toFixed(1)}
                                    <span className="scale-digit-unit">gr</span>
                                </div>
                            </div>

                            {/* Progress bar visualizer */}
                            <div className="scale-progress-bar-wrap">
                                <div 
                                    className={`scale-progress-bar ${isNextStepAvailable() ? 'in-range' : (currentTotalWeight > currentTargetQty ? 'over' : 'under')}`}
                                    style={{ width: `${Math.min(progressPercent, 100)}%` }}
                                ></div>
                            </div>

                            {tempWeighings.length > 0 && (
                                <div className="p-2 mb-3 bg-black bg-opacity-25 rounded text-center small text-light">
                                    Προσωρινά ζυγίσματα: {tempWeighings.join(' + ')} gr {weight > 0 ? `(+ ${weight} gr)` : ''}
                                </div>
                            )}

                            {/* Action Control Panel */}
                            <div className="d-flex gap-3 justify-content-center pt-2">
                                <Button color="info" size="lg" onClick={addTempWeighing} title="Προσθήκη προσωρινού βάρους και μηδενισμός">
                                    <i className="bi bi-plus-circle"></i> Κράτημα (+Tare)
                                </Button>
                                
                                {!isFinishAvailable() && (
                                    <Button 
                                        color={isNextStepAvailable() ? "success" : "primary"} 
                                        size="lg" 
                                        onClick={nextStep}
                                        disabled={!isNextStepAvailable()}
                                    >
                                        <i className="bi bi-arrow-right-circle-fill"></i> Επόμενο Υλικό
                                    </Button>
                                )}

                                {isFinishAvailable() && (
                                    <Button 
                                        color="success" 
                                        size="lg" 
                                        onClick={completeCommand}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-check-circle-fill"></i> Ολοκλήρωση Παραγωγής
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* Step by Step Navigator */}
                    <Col lg="4">
                        <div className="ek-card">
                            <CardHeader className="ek-card-header">
                                <h5>
                                    <i className="bi bi-list-ol text-primary"></i> Βήματα Συνταγής
                                </h5>
                                <Badge color="primary" pill>{step + 1} / {qtys.length}</Badge>
                            </CardHeader>
                            <CardBody className="ek-card-body p-3 scrollable-list">
                                {qtys.map((q, idx) => {
                                    const ing = ingredients.find(i => i.id === q.ingredientId);
                                    const isDone = actualQtys.some(aq => aq.ingredientId === q.ingredientId);
                                    const isCurrent = idx === step;
                                    const weighedQty = actualQtys.find(aq => aq.ingredientId === q.ingredientId)?.qty;

                                    return (
                                        <div 
                                            key={idx} 
                                            className={`step-item-card ${isCurrent ? 'current' : ''} ${isDone ? 'completed' : ''}`}
                                            onClick={() => setStep(idx)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <div className={`step-badge ${isDone ? 'completed' : (isCurrent ? 'current' : 'pending')}`}>
                                                    {isDone ? <i className="bi bi-check"></i> : idx + 1}
                                                </div>
                                                <div>
                                                    <div className="fw-semibold small">{ing ? ing.title : 'Συστατικό'}</div>
                                                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        Στόχος: {q.qty} gr
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                {isDone ? (
                                                    <span className="badge bg-success">{weighedQty?.toFixed(1)} gr</span>
                                                ) : isCurrent ? (
                                                    <span className="badge bg-primary">Τρέχον</span>
                                                ) : (
                                                    <span className="badge bg-light text-muted">Εκκρεμεί</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="mt-4 pt-3 border-top d-flex flex-column gap-2">
                                    <Button color="outline-primary" block size="sm" onClick={pauseAndExit}>
                                        <i className="bi bi-pause-circle me-1"></i> Προσωρινή Παύση & Έξοδος
                                    </Button>
                                    <Button color="outline-danger" block size="sm" onClick={cancelAndResetRun}>
                                        <i className="bi bi-trash me-1"></i> Ακύρωση & Επανεκκίνηση
                                    </Button>
                                </div>
                            </CardBody>
                        </div>
                    </Col>
                </Row>
                ) : (
                    <div className="ek-card p-5 text-center">
                        <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                        <h4 className="mt-3">Δεν βρέθηκαν συστατικά για ζύγιση</h4>
                        <p className="text-muted">Η συνταγή "{recipy?.title || command.recipeTitle || 'Συνταγή'}" δεν έχει καταχωρημένα συστατικά.</p>
                        <div className="mt-3">
                            <Button color="primary" onClick={() => setCommand({})}>
                                <i className="bi bi-arrow-left me-1"></i> Επιστροφή στις Εντολές
                            </Button>
                        </div>
                    </div>
                )
            )}

            {/* 4. Completed Order View */}
            {command.id && command.completed && (
                <div className="ek-card p-4 text-center">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                    <h3 className="mt-3 text-dark fw-bold">Η Παραγωγή Ολοκληρώθηκε Επιτυχώς!</h3>
                    <p className="text-muted">Η εντολή <strong>{command.title}</strong> καταγράφηκε στο σύστημα παραγωγής.</p>
                    
                    <div className="my-4 mx-auto" style={{ maxWidth: '400px' }}>
                        <div className="p-3 bg-light rounded-3 border text-start">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Συνταγή:</span>
                                <strong>{recipy?.title}</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Συνολικό Βάρος:</span>
                                <strong className="text-success">{command.weight || command.qty} gr</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Συστατικά:</span>
                                <strong>{actualQtys.length} ζυγίστηκαν</strong>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-center gap-2">
                        <Button color="primary" onClick={() => { setCommand({}); loadData(); }}>
                            <i className="bi bi-arrow-left"></i> Επιστροφή στις Εντολές
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export { Run };
export default Run;
